import {
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updatePassword
} from 'firebase/auth';
import { auth } from './firebase.js';
import { acceptDriveAuthorization, DRIVE_READ_SCOPE, DRIVE_SCOPE } from './drive-authoring.js';

const STUDENT_HANDLE_PATTERN = /^[A-Z][A-Z0-9]{2,31}$/;
const PIN_PATTERN = /^\d{6}$/;
const studentEmailDomain = import.meta.env.VITE_STUDENT_EMAIL_DOMAIN || 'students.example-app.vn';

export function normalizeHandle(value) {
  return String(value || '')
    .trim()
    .replace(/[đĐ]/g, character => character === 'đ' ? 'd' : 'D')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

export function assertValidHandle(handle) {
  if (!STUDENT_HANDLE_PATTERN.test(handle)) throw new Error('Tên đăng nhập hoặc PIN không đúng');
  return handle;
}

export function handleToInternalEmail(handle) {
  return `${assertValidHandle(normalizeHandle(handle)).toLowerCase()}@${studentEmailDomain}`;
}

export async function loginStudent(handle, pin) {
  if (!PIN_PATTERN.test(String(pin || ''))) throw new Error('Tên đăng nhập hoặc PIN không đúng');
  await setPersistence(auth, browserSessionPersistence);
  try {
    return await signInWithEmailAndPassword(auth, handleToInternalEmail(handle), String(pin));
  } catch (_error) {
    throw new Error('Tên đăng nhập hoặc PIN không đúng');
  }
}

export async function loginCreator() {
  await setPersistence(auth, browserLocalPersistence);
  const provider = new GoogleAuthProvider();
  provider.addScope(DRIVE_SCOPE);
  provider.addScope(DRIVE_READ_SCOPE);
  const result = await signInWithPopup(auth, provider);
  const credential = GoogleAuthProvider.credentialFromResult(result);
  if (credential?.accessToken) acceptDriveAuthorization(credential.accessToken, result.user);
  return result;
}

export async function changeStudentPin(currentUser, nextPin) {
  if (!PIN_PATTERN.test(String(nextPin || ''))) throw new Error('PIN mới phải gồm đúng 6 chữ số.');
  await updatePassword(currentUser, String(nextPin));
}

export function observeSession(callback) {
  return onAuthStateChanged(auth, async user => {
    try {
      if (user?.providerData.some(provider => provider.providerId === 'google.com')) {
        await setPersistence(auth, browserLocalPersistence);
      }
    } finally {
      callback(user);
    }
  });
}

export function logout() {
  return signOut(auth);
}

export function getAccountType(user) {
  return user.getIdTokenResult().then(result => result.claims.accountType === 'student' ? 'student' : 'creator');
}
